import { atom, useRecoilState } from "recoil";

import RestApiCall from "interceptor/aeh";
import { API_FETCH_STATUS, PROFILE_TYPES } from "constants/common";
import Environment from "services/environment";
import { requirePartyId } from "utils/partyIdUtils";

const env = Environment.getEnv();

const { SUCCESS, LOADING, ERROR } = API_FETCH_STATUS;

const fetchProductsAPI = ({ params }) => {
  const apiPayload = {
    method: "GET",
    url: "connection/v1/inventory/details",
    baseURL: env.connectionBaseUrl,
    config: { ivUser: env.ivUser },
    params,
  };
  return RestApiCall.api(apiPayload);
};

const fetchPartnerProductsAPI = (payload) => {
  let apiPayload = {
    method: "POST",
    url: "v1/fetch/widget/details",
    baseURL: env.bffBaseUrl,
    params: { segmentId: payload.segmentId },
    data: payload,
    config: { ivUser: env.ivUser, ignoreError: true },
  };
  return RestApiCall.api(apiPayload);
};

const getLobList = (products) => {
  const lobMap = {};
  products.forEach((product) => {
    const { lob, lobDisplayName, productDisplayName, productType } = product;

    const addLobProduct = (isNewEntry) => {
      const products = [];
      if (productDisplayName && productType) {
        products.push({ productDisplayName, productType });
      }

      lobMap[lob].products = isNewEntry ? [...products] : [...lobMap[lob].products, ...products];
    };

    if (lob in lobMap) {
      addLobProduct(false);
    } else {
      const isPartySupportedLob = requirePartyId(lob);

      lobMap[lob] = {
        key: lob,
        displayName: lobDisplayName,
        isPartySupportedLob,
        urlKey: "selfcareBasePageUrl",
      };

      addLobProduct(true);
    }
  });

  return Object.values(lobMap);
};

const PRODUCTS_INITIAL_STATE = {
  state: null,
  productStatus: null,
  lobs: null,
  lob: null,
  lobDetails: null,
  products: null,
  product: null,
  productDetails: null,
  partnerProducts: null,
};

const productsAtom = atom({
  key: "products",
  default: { ...PRODUCTS_INITIAL_STATE },
});

const useProducts = () => {
  const [productsState, setProductsState] = useRecoilState(productsAtom);

  const { productStatus, lobs, lob, lobDetails, products, product, productDetails, partnerProducts } = productsState;

  const updateStateProperties = (updatedProperties) => {
    setProductsState((state) => ({ ...state, ...updatedProperties }));
  };

  const fetchSelectedLob = ({ lobs, lob: lobFromParam, isAuth = true }) => {
    if (isAuth) {
      if (!lobs) {
        return;
      }
      if (!lobFromParam) {
        updateStateProperties({ lob: null, lobDetails: null });
        return;
      }
      if (lobFromParam !== lob) {
        const lobDetails = lobs.find((i) => i.key === lobFromParam.toUpperCase());
        if (lobDetails) {
          updateStateProperties({ lob: lobDetails.key, lobDetails });
        } else {
          updateStateProperties({ lob: null, lobDetails: null });
        }
      }
    } else {
      updateStateProperties({ lob: lobFromParam, lobDetails: null });
    }
  };

  const fetchSelectedProduct = ({ products, product: productFromParam }) => {
    if (!products) {
      return;
    }
    if (!productFromParam) {
      updateStateProperties({ product: null, productDetails: null });
      return;
    }
    if (productFromParam !== product) {
      const productDetails = products.find((i) => i.productType === productFromParam);
      if (productDetails) {
        updateStateProperties({ product: productDetails.productType, productDetails });
      } else {
        updateStateProperties({ product: null, productDetails: null });
      }
    }
  };

  const fetchProducts = ({ partyId, userProfileType } = {}) => {
    const params = {};
    const isAtbSkam = userProfileType === PROFILE_TYPES.ATB_SECONDARY_KAM_USER;
    if (isAtbSkam) {
      params.partyId = partyId;
    }
    updateStateProperties({ productStatus: LOADING });
    return new Promise((resolve, reject) => {
      fetchProductsAPI({ params })
        .then((res) => {
          const products = res.data.data;
          const lobs = getLobList(products);
          updateStateProperties({ products, lobs, productStatus: SUCCESS });
          resolve({ lobs, products });
        })
        .catch((err) => {
          updateStateProperties({ productStatus: ERROR });
          resolve({ lobs: null, products: null });
        });
    });
  };

  const fetchPartnerProducts = () => {
    const payload = { segmentId: "subscribedProductsV1" };
    updateStateProperties({ state: LOADING });
    return new Promise((resolve, reject) => {
      fetchPartnerProductsAPI(payload)
        .then((res) => {
          const partnerProducts = res.data.data;
          updateStateProperties({ partnerProducts, state: SUCCESS });
          resolve({ partnerProducts });
        })
        .catch((err) => {
          updateStateProperties({ state: ERROR });
          resolve({ partnerProducts: null });
        });
    });
  };

  const getLobDetails = (lobFromParam) => {
    if (lobFromParam && lobs) {
      const lobFromProducts = lobs.find((lob) => lob.key === lobFromParam.toUpperCase());
      if (lobFromProducts) {
        return lobFromProducts;
      }
      return null;
    }
    return null;
  };

  const getProductDetails = (productFromParam) => {
    if (products) {
      const productDetails = products.find((i) => i.productType === productFromParam);
      if (productDetails) {
        return productDetails;
      }
      return null;
    }
    return null;
  };

  const setLobDetails = (lobDetails) => {
    updateStateProperties({ lob: lobDetails.key, lobDetails });
  };

  const setProductDetails = (productDetails) => {
    const lobFromProducts = lobs.find((i) => i.key === productDetails.lob.toUpperCase());

    const { key, products } = lobFromProducts;

    const { productType, productDisplayName } = products.find((i) => i.productType === productDetails.productType);

    updateStateProperties({
      lob: key,
      product: productType,
      productDetails: {
        key: productType,
        displayName: productDisplayName,
      },
    });
  };

  return {
    productStatus,
    lobs,
    lob,
    lobDetails,
    products,
    product,
    productDetails,
    partnerProducts,
    getLobDetails,
    getProductDetails,
    setLobDetails,
    setProductDetails,
    fetchProducts,
    fetchPartnerProducts,
    fetchSelectedLob,
    fetchSelectedProduct,
  };
};

export default useProducts;
